import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    phone_alt: '',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotValue = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim() || '';
    if (honeypotValue) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u547dca23r0tr1aqs0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '', phone_alt: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg =
          ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | null)?.message ||
          ((parsed as Record<string, unknown> | null)?.message as string) ||
          ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | null)?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '眠境',
    description: '专业的助眠 ASMR 内容精选平台，用声音的力量帮助每个人获得更好的睡眠。',
    url: 'https://runbai666.cn/',
    email: 'hello@mianjing-sleep.com',
    foundingDate: '2024',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://runbai666.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://runbai666.cn/about',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors no-underline">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>

            <div className="text-center mb-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于<strong className="text-primary-600">眠境</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                用声音的力量，守护每一个安睡的夜晚
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-12 mb-20">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong>眠境</strong>成立于2024年，是国内专注<strong>助眠 ASMR</strong>内容精选的专业平台。
                    我们的创始团队由睡眠健康研究者、音频工程师和内容策划人组成，
                    致力于为失眠人群提供高质量的<strong>助眠 ASMR 音频</strong>与视频推荐。
                  </p>
                  <p>
                    在快节奏的都市生活中，越来越多的人面临入睡困难、睡眠质量下降的问题。
                    眠境通过精心筛选和整理各类<strong>ASMR 助眠白噪音</strong>、<strong>ASMR 耳语助眠</strong>、
                    敲击音、自然音等内容，帮助用户找到最适合自己的<strong>深度睡眠 ASMR</strong>体验。
                  </p>
                  <p>
                    我们相信，好的睡眠不需要依赖药物。通过科学的声音疗愈，
                    每个人都可以重新获得自然入睡的能力。眠境将持续更新和优化内容库，
                    为每一位<strong>失眠助眠 ASMR</strong>用户提供最优质的内容服务。
                  </p>
                </div>
              </div>

              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20minimalist%20bedroom%20with%20soft%20warm%20amber%20lighting%2C%20peaceful%20atmosphere%2C%20cream%20and%20beige%20bedding%2C%20gentle%20moonlight%20through%20window%2C%20dreamy%20soft%20focus%2C%20cinematic%20photography%2C%20tranquil%20mood%2C%20luxury%20minimalist%20aesthetic&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="眠境 - 助眠 ASMR 品牌"
                  title="眠境助眠 ASMR 品牌故事"
                  className="w-full rounded-2xl object-cover aspect-[4/3]"
                />
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
                为什么选择<strong className="text-primary-600">眠境</strong>？
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  {
                    icon: 'ri-check-double-line',
                    title: '精选内容',
                    desc: '专业团队精心筛选每一条助眠 ASMR 内容，确保音质与效果。',
                  },
                  {
                    icon: 'ri-group-line',
                    title: '社区口碑',
                    desc: '已有超过50万用户通过眠境改善了睡眠质量，好评率98%。',
                  },
                  {
                    icon: 'ri-lightbulb-line',
                    title: '科学指导',
                    desc: '基于睡眠科学和声学原理，提供科学的 ASMR 收听建议。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全可靠',
                    desc: '所有内容均经过审核，无毒无害，适合成人睡前使用。',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-xl bg-background-100 p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                  联系我们
                </h2>
                <div className="space-y-4 text-sm text-foreground-600">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-primary-500"></i>
                    hello@mianjing-sleep.com
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-wechat-line text-primary-500"></i>
                    微信公众号：眠境ASMR
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-bilibili-line text-primary-500"></i>
                    B站：眠境ASMR官方
                  </p>
                </div>
              </div>

              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                  留言咨询
                </h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="请输入您的留言（最多500字）"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl text-sm bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    value={formData.phone_alt}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">留言已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}